import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { submitInquiry } from "@/lib/api";
import { INQUIRY_OPTIONS } from "@/lib/content";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = { name: "", email: "", company: "", category: "", message: "" };

export default function PartnershipForm() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.category) {
      toast.error("Please select an inquiry type.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitInquiry(form);
      toast.success(res.message || "Your inquiry has been received.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "h-13 w-full rounded-xl border border-white/12 bg-rp-card px-4 py-3.5 text-sm text-white placeholder:text-white/35 outline-none transition-colors duration-200 focus:border-rp-accent focus:ring-1 focus:ring-rp-accent";

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="partnership-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.14em] text-white/50">Name *</label>
          <input required value={form.name} onChange={set("name")} placeholder="Your name" className={field} data-testid="inquiry-name" />
        </div>
        <div>
          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.14em] text-white/50">Email *</label>
          <input required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={field} data-testid="inquiry-email" />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.14em] text-white/50">Company / Organization</label>
          <input value={form.company} onChange={set("company")} placeholder="Optional" className={field} data-testid="inquiry-company" />
        </div>
        <div>
          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.14em] text-white/50">Inquiry Type *</label>
          <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
            <SelectTrigger
              data-testid="inquiry-category"
              className="h-[52px] rounded-xl border-white/12 bg-rp-card px-4 text-sm text-white focus:border-rp-accent focus:ring-1 focus:ring-rp-accent data-[placeholder]:text-white/35"
            >
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-rp-surface text-white">
              {INQUIRY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-white focus:bg-rp-accent focus:text-black" data-testid={`inquiry-option-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.14em] text-white/50">Message *</label>
        <textarea
          required
          rows={5}
          value={form.message}
          onChange={set("message")}
          placeholder="Tell us about the opportunity you have in mind."
          className={`${field} h-auto resize-none`}
          data-testid="inquiry-message"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="inquiry-submit"
        className="group inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-rp-accent px-8 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-black transition-colors duration-200 hover:bg-white disabled:opacity-70 sm:w-auto"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? "Sending" : "Submit Inquiry"}
      </button>
    </form>
  );
}
